import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Plus, Trash2 } from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type ContactPurchase,
  type ProspectDetail,
  type ProspectListContactEntry,
  type ProspectListMembership,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { ORG_CRM_READ_SCOPE } from "../../../auth/orgRoutes.ts";
import { userHasOrgScope } from "../../../auth/scopes.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import { useUsers } from "../../../hooks/resources.ts";
import AddContactDialog from "../../AddContactDialog.tsx";
import ConfirmDialog from "../../ConfirmDialog.tsx";
import FormattedDate from "../../FormattedDate.tsx";
import StatusChip from "../../StatusChip.tsx";
import AddLabelPopover from "./AddLabelPopover.tsx";
import CompanyContactsPanel from "./CompanyContactsPanel.tsx";
import { type CollectionCompany, companyKey } from "./entryStatus.ts";
import FindContactsDialog from "./FindContactsDialog.tsx";
import LinkCompanyDialog from "./LinkCompanyDialog.tsx";
import { useContactEntryMutations } from "./useContactEntryMutations.ts";
import { useCrmStatus } from "./useCrmStatus.ts";

/** The entry as the collection's dialogs and panels take it. */
function entryCompany(prospect: ProspectDetail, membership: ProspectListMembership): CollectionCompany {
  return {
    id: membership.entry_id,
    master_company_id: prospect.master_company_id,
    company_name: prospect.company_name,
    company_country: prospect.company_country,
    company_domain: prospect.company_domain,
    company_org_number: membership.company_org_number,
    company_website: membership.company_website,
  };
}

function useUserName() {
  const { data: users } = useUsers();
  return (userId: string | null, agentRunId: string | null, method?: string) => {
    if (agentRunId || method === "agent") return "an AI agent";
    if (userId) {
      const u = users?.find((x) => x.id === userId);
      return u?.display_name?.trim() || u?.email || "someone";
    }
    return method === "mcp" ? "an MCP client" : "someone";
  };
}

/**
 * The prospect's entry on the collection the drawer was opened from: how it
 * got there, why, its labels, what it misses, and the entry's own actions
 * (link an unresolved company, remove it from the collection).
 */
export function CollectionEntrySection({
  prospect,
  membership,
  canEdit,
  onGone,
}: {
  prospect: ProspectDetail;
  membership: ProspectListMembership;
  canEdit: boolean;
  /** The entry is no longer this prospect's on the collection (removed, or linked away). */
  onGone: () => void;
}) {
  const queryClient = useQueryClient();
  const listId = membership.collection_id;
  const entryId = membership.entry_id;
  const nameOf = useUserName();
  const [labelAnchor, setLabelAnchor] = useState<HTMLElement | null>(null);
  const [labelKey, setLabelKey] = useState("");
  const [labelValue, setLabelValue] = useState("");
  const [linking, setLinking] = useState(false);
  const [removing, setRemoving] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: keys.prospect.all });
    queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(listId) });
  };
  const labels = useMutation({
    mutationFn: (data: { set_labels?: Record<string, string>; remove_label_keys?: string[] }) =>
      api.updateProspectLabels(listId, entryId, data),
    onSuccess: (_entry, data) => {
      refresh();
      if (data.set_labels) {
        setLabelAnchor(null);
        setLabelKey("");
        setLabelValue("");
      }
    },
    // Adding shows its error in the popover; removing toasts.
    meta: { suppressErrorToast: true },
  });
  const remove = useMutation({
    mutationFn: () => api.removeProspectFromList(listId, entryId),
    onSuccess: () => {
      refresh();
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      toast.success(`Removed from ${membership.collection_name}`);
      setRemoving(false);
      onGone();
    },
  });

  const addedBy = nameOf(membership.added_by_user_id, membership.added_by_agent_run_id, membership.added_with_method);
  const entries = Object.entries(membership.labels);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            In {membership.collection_name}
          </Typography>
          {membership.archived && <Chip size="small" variant="outlined" label="Archived" />}
          <StatusChip kind="prospect_status" value={membership.review_state} variant="outlined" />
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Added <FormattedDate value={membership.created_at} /> by {addedBy}
        </Typography>
        {membership.inclusion_reason && <Typography variant="body2">{membership.inclusion_reason}</Typography>}
        {membership.review_state === "disqualified" && membership.disqualified_at && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Disqualified <FormattedDate value={membership.disqualified_at} /> by{" "}
            {nameOf(membership.disqualified_by_user_id, membership.disqualified_by_agent_run_id)}
            {membership.disqualification_reason && <>: &ldquo;{membership.disqualification_reason}&rdquo;</>}
          </Typography>
        )}
        {membership.missing_required_properties.length > 0 && membership.review_state !== "disqualified" && (
          <Alert severity="warning" sx={{ py: 0 }}>
            Missing required: {membership.missing_required_properties.join(", ")}
          </Alert>
        )}

        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          {entries.map(([key, value]) => (
            <Chip
              key={key}
              size="small"
              label={`${key}: ${value}`}
              onDelete={
                canEdit
                  ? () =>
                      labels.mutate(
                        { remove_label_keys: [key] },
                        { onError: (err) => toast.error(getErrorMessage(err)) },
                      )
                  : undefined
              }
            />
          ))}
          {entries.length === 0 && !canEdit && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              No labels
            </Typography>
          )}
          {canEdit && (
            <Button
              size="small"
              startIcon={<Plus fontSize="small" />}
              onClick={(e) => {
                labels.reset();
                setLabelAnchor(e.currentTarget);
              }}
            >
              Add label
            </Button>
          )}
        </Stack>

        {canEdit && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            {!prospect.resolved && (
              <Button size="small" variant="outlined" startIcon={<Link2 fontSize="small" />} onClick={() => setLinking(true)}>
                Link to company
              </Button>
            )}
            <Button
              size="small"
              color="error"
              startIcon={<Trash2 fontSize="small" />}
              onClick={() => setRemoving(true)}
            >
              Remove from collection
            </Button>
          </Stack>
        )}
      </Stack>

      <AddLabelPopover
        anchorEl={labelAnchor}
        onClose={() => setLabelAnchor(null)}
        labelKey={labelKey}
        labelValue={labelValue}
        onKeyChange={setLabelKey}
        onValueChange={setLabelValue}
        onSubmit={() => {
          if (!labelKey.trim() || !labelValue.trim()) return;
          labels.mutate({ set_labels: { [labelKey.trim()]: labelValue.trim() } });
        }}
        submitting={labels.isPending}
        error={labels.error && labelAnchor ? getErrorMessage(labels.error) : null}
      />
      <LinkCompanyDialog
        listId={listId}
        entry={linking ? entryCompany(prospect, membership) : null}
        onClose={() => setLinking(false)}
        onLinked={onGone}
      />
      <ConfirmDialog
        open={removing}
        title={`Remove ${prospect.company_name ?? "this company"} from ${membership.collection_name}?`}
        confirmLabel="Remove"
        color="error"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setRemoving(false)}
      >
        The company stays in the database and on its other collections; its people on this collection go with it.
      </ConfirmDialog>
    </Paper>
  );
}

/**
 * The people at the company on the collection the drawer was opened from,
 * with the collection's contact actions: add one, find some (data providers), notes,
 * decisions, CRM state. A card opens its person beside the drawer.
 */
export function CollectionContactsSection({
  prospect,
  membership,
  openEntryId,
  onOpenContact,
}: {
  prospect: ProspectDetail;
  membership: ProspectListMembership;
  openEntryId?: string | null;
  onOpenContact: (contact: ProspectListContactEntry, el: HTMLElement) => void;
}) {
  const listId = membership.collection_id;
  const company = entryCompany(prospect, membership);
  const { user } = useAuth();
  const { selectedOrgId } = useOrganization();
  const canBuyContacts = userHasOrgScope(user, selectedOrgId, "enrichment:purchase");
  const canReadCrm = userHasOrgScope(user, selectedOrgId, ORG_CRM_READ_SCOPE);
  const [adding, setAdding] = useState(false);
  const [finding, setFinding] = useState(false);

  const contacts: ProspectListContactEntry[] = prospect.contacts.filter((c) => c.collection_id === listId);
  const mutations = useContactEntryMutations(listId);
  const crm = useCrmStatus(listId, canReadCrm);
  const purchases = useQuery({
    queryKey: keys.contactEnrichmentJob.byList(selectedOrgId ?? "", listId),
    queryFn: () => api.listContactPurchases(selectedOrgId!, { listId }),
    enabled: !!selectedOrgId && canBuyContacts,
    meta: { live: true },
  });
  // Newest first from the API, so the first one at the company is the latest.
  const key = companyKey(company);
  const purchase: ContactPurchase | undefined = purchases.data?.find(
    (job) => (job.master_company_id ?? (job.collection_entry_id ? `entry:${job.collection_entry_id}` : null)) === key,
  );

  return (
    <Box>
      <CompanyContactsPanel
        title={`Contacts in ${membership.collection_name}`}
        prospect={company}
        contacts={contacts}
        loading={false}
        mutations={mutations}
        crm={crm}
        onAddContact={() => setAdding(true)}
        onFindContacts={canBuyContacts ? () => setFinding(true) : undefined}
        purchase={purchase}
        openEntryId={openEntryId}
        onOpenContact={onOpenContact}
      />
      {adding && (
        <AddContactDialog
          onClose={() => setAdding(false)}
          listId={listId}
          companyId={prospect.master_company_id}
          companyEntryId={prospect.master_company_id ? null : membership.entry_id}
        />
      )}
      {selectedOrgId && canBuyContacts && (
        <FindContactsDialog
          orgId={selectedOrgId}
          listId={listId}
          prospect={finding ? company : null}
          onClose={() => setFinding(false)}
          onStarted={() => purchases.refetch()}
        />
      )}
    </Box>
  );
}
